import { sql } from '../base';
import { TimeEntry, Task } from '../types';

async function recalculateTaskTimeSpent(taskId: string): Promise<number> {
  const totalResult = await sql`
    SELECT COALESCE(SUM(
      CASE WHEN end_time IS NULL THEN 0
           ELSE CEIL(EXTRACT(EPOCH FROM (end_time - start_time)) / 60.0)
      END
    ), 0)::INTEGER AS total_minutes
    FROM time_entries
    WHERE task_id = ${taskId}
  `;

  const totalMinutes = totalResult[0]?.total_minutes || 0;

  await sql`
    UPDATE tasks
    SET time_spent = ${totalMinutes},
        time_spent_minutes = ${totalMinutes},
        updated_at = now()
    WHERE id = ${taskId}
  `;

  return totalMinutes;
}

const timeEntriesModule = {
  async findByTaskId(taskId: string, userId: string): Promise<TimeEntry[]> {
    try {
      const result = await sql`
        SELECT te.* FROM time_entries te
        JOIN tasks t ON te.task_id = t.id
        WHERE te.task_id = ${taskId} AND (t.user_id = ${userId} OR t.assigned_to = ${userId})
        ORDER BY te.start_time DESC
      `;
      return (result || []) as TimeEntry[];
    } catch (error) {
      console.error('Error fetching time entries:', error);
      return [];
    }
  },

  async create(data: Omit<TimeEntry, 'id' | 'created_at'>): Promise<TimeEntry> {
    let endTime = data.end_time;
    if (data.start_time && data.end_time) {
      const startDate = new Date(data.start_time);
      const endDate = new Date(data.end_time);
      const durationMs = endDate.getTime() - startDate.getTime();
      if (durationMs < 60000) {
        endTime = new Date(startDate.getTime() + 60000);
      }
    }

    const result = await sql`
      INSERT INTO time_entries (task_id, user_id, start_time, end_time, notes)
      VALUES (${data.task_id}, ${data.user_id}, ${data.start_time}, ${endTime}, ${data.notes})
      RETURNING *
    `;

    const timeEntry = result[0] as TimeEntry;
    await recalculateTaskTimeSpent(timeEntry.task_id);
    return timeEntry;
  },

  async update(id: string, userId: string, data: Partial<TimeEntry>): Promise<TimeEntry> {
    const result = await sql`
      UPDATE time_entries
      SET end_time = COALESCE(${data.end_time}, end_time),
          notes = COALESCE(${data.notes}, notes)
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;

    const updatedEntry = result[0] as TimeEntry;

    if (updatedEntry?.task_id) {
      await recalculateTaskTimeSpent(updatedEntry.task_id);
    }

    return updatedEntry;
  },

  async delete(id: string, userId: string): Promise<boolean> {
    const result = await sql`
      DELETE FROM time_entries
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING task_id
    `;

    if (result.length > 0 && result[0]?.task_id) {
      await recalculateTaskTimeSpent(result[0].task_id);
    }

    return result.length > 0;
  },

  async getTotalMinutesForTask(taskId: string): Promise<number> {
    const result = await sql`
      SELECT COALESCE(SUM(
        CASE WHEN end_time IS NULL THEN 0
             ELSE CEIL(EXTRACT(EPOCH FROM (end_time - start_time)) / 60.0)
        END
      ), 0)::INTEGER AS total_minutes
      FROM time_entries
      WHERE task_id = ${taskId}
    `;
    return result[0]?.total_minutes || 0;
  },

  async getTotalTimeByGoalId(goalId: string, userId: string): Promise<number> {
    try {
      const result = await sql`
        SELECT COALESCE(SUM(
          CASE
            WHEN te.end_time IS NULL THEN EXTRACT(EPOCH FROM (NOW() - te.start_time))
            ELSE EXTRACT(EPOCH FROM (te.end_time - te.start_time))
          END
        ), 0)::BIGINT AS total_seconds
        FROM time_entries te
        JOIN tasks t ON te.task_id = t.id
        WHERE t.goal_id = ${goalId}
          AND (t.user_id = ${userId} OR t.assigned_to = ${userId})
      `;

      const totalSeconds = Number(result[0]?.total_seconds || 0);
      return Number.isFinite(totalSeconds) ? totalSeconds : 0;
    } catch (error) {
      console.error('Error calculating total time for goal:', { goalId, userId, error });
      return 0;
    }
  },

  async deleteByTaskId(taskId: string): Promise<void> {
    await sql`
      DELETE FROM time_entries
      WHERE task_id = ${taskId}
    `;
  }
};

export type TimeEntriesModule = typeof timeEntriesModule;

export function registerTimeEntries(db: { timeEntries?: TimeEntriesModule }) {
  db.timeEntries = timeEntriesModule;
}

export { timeEntriesModule as timeEntries, recalculateTaskTimeSpent };
